import { useEffect, useState } from "react";
import { Plus, Loader2, Hash, Lock, Globe, Trash2, Check, X, Inbox, Pencil, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { AdminHeader, TableWrap, Th, Td, Tr, StatusChip, Spinner, EmptyRow, Modal, Field } from "@/components/admin/ui";

const emptyForm = { name: "", description: "", is_public: true };

export default function AdminChannels() {
  const [rooms, setRooms] = useState([]);
  const [requests, setRequests] = useState([]);
  const [tentatives, setTentatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");

  const load = () => {
    Promise.all([apiClient.get("/chat/rooms"), apiClient.get("/admin/chat/channel-requests"), apiClient.get("/admin/tentative")])
      .then(([r, req, t]) => { setRooms(r.data); setRequests(req.data); setTentatives(t.data); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Channel name is required"); return; }
    setSaving(true);
    try {
      if (form.id) {
        await apiClient.put(`/admin/chat/rooms/${form.id}`, { name: form.name.trim(), description: form.description.trim() || null, is_public: form.is_public });
        toast.success("Channel updated");
      } else {
        await apiClient.post("/admin/chat/rooms", { name: form.name.trim(), description: form.description.trim() || null, is_public: form.is_public });
        toast.success("Channel created");
      }
      setModalOpen(false); setForm(emptyForm); load();
    } catch (err) { toast.error(getErrorMessage(err, "Save failed")); } finally { setSaving(false); }
  };

  const deactivate = async (id, name) => {
    if (!window.confirm(`Deactivate channel "${name}"? Message history is preserved.`)) return;
    setBusy(id);
    try { await apiClient.delete(`/admin/chat/rooms/${id}`); toast.success("Channel deactivated"); load(); }
    catch (err) { toast.error(getErrorMessage(err, "Failed")); } finally { setBusy(""); }
  };

  const handleReq = async (id, action) => {
    setBusy(id);
    try {
      await apiClient.post(`/admin/chat/channel-requests/${id}/${action}`);
      toast.success(action === "approve" ? "Request approved — channel created" : "Request rejected");
      load();
    } catch (err) { toast.error(getErrorMessage(err, "Failed")); } finally { setBusy(""); }
  };

  const deleteTentative = async (t) => {
    if (!window.confirm(`Delete the suggested session by ${t.organizer_name} in ${t.room_name}? This can't be undone.`)) return;
    setBusy(t.id);
    try { await apiClient.delete(`/admin/tentative/${t.id}`); toast.success("Suggested session deleted"); load(); }
    catch (err) { toast.error(getErrorMessage(err, "Failed")); } finally { setBusy(""); }
  };

  const TENT_STATUS = { open: { label: "Open", color: "#00e5ff" }, converted: { label: "Converted", color: "#00ff9d" }, cancelled: { label: "Cancelled", color: "#94a3b8" } };

  if (loading) return <Spinner label="Loading channels" />;

  const pending = requests.filter((r) => r.status === "pending");

  return (
    <div>
      <AdminHeader title="Channels" subtitle={`${rooms.length} active channels · ${pending.length} pending request${pending.length === 1 ? "" : "s"}`}
        right={<button onClick={() => { setForm(emptyForm); setModalOpen(true); }} className="neon-btn px-5 py-2.5" data-testid="add-channel"><Plus className="w-4 h-4" /> Create Channel</button>} />

      {/* Channel requests */}
      <div className="glass rounded-2xl p-6 mb-8" data-testid="channel-requests-panel">
        <h3 className="font-display text-lg font-bold text-white mb-4 flex items-center gap-2"><Inbox className="w-5 h-5" style={{ color: "#ffb020" }} /> Channel Requests</h3>
        {requests.length === 0 ? (
          <p className="text-slate-500 py-4">No channel requests yet.</p>
        ) : (
          <div className="space-y-3">
            {requests.map((r) => (
              <div key={r.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl px-4 py-3 border border-white/10" data-testid={`channel-request-${r.id}`}>
                <div>
                  <p className="text-white font-semibold flex items-center gap-2"><Lock className="w-3.5 h-3.5 text-slate-400" />{r.name}</p>
                  <p className="text-slate-500 text-xs">by {r.user_name} · {r.user_email}{r.reason ? ` — "${r.reason}"` : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  {r.status === "pending" ? (
                    <>
                      <button onClick={() => handleReq(r.id, "approve")} disabled={busy === r.id} className="neon-btn px-4 py-2 text-sm" data-testid={`request-approve-${r.id}`} style={{ background: "linear-gradient(90deg, #00ff9d, #00e5ff)", borderColor: "rgba(0,255,157,0.6)" }}>
                        {busy === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Approve</>}
                      </button>
                      <button onClick={() => handleReq(r.id, "reject")} disabled={busy === r.id} className="neon-outline-btn px-4 py-2 text-sm" data-testid={`request-reject-${r.id}`}>
                        <X className="w-4 h-4" /> Reject
                      </button>
                    </>
                  ) : <StatusChip status={r.status === "approved" ? "confirmed" : "cancelled"} />}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Suggested (tentative) sessions */}
      <div className="glass rounded-2xl p-6 mb-8" data-testid="tentative-sessions-panel">
        <h3 className="font-display text-lg font-bold text-white mb-4 flex items-center gap-2"><CalendarPlus className="w-5 h-5" style={{ color: "#8b5cf6" }} /> Suggested Sessions</h3>
        {tentatives.length === 0 ? (
          <p className="text-slate-500 py-4">No suggested sessions yet.</p>
        ) : (
          <div className="space-y-3">
            {tentatives.map((t) => {
              const st = TENT_STATUS[t.status] || { label: t.status, color: "#94a3b8" };
              return (
                <div key={t.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl px-4 py-3 border border-white/10" data-testid={`tentative-row-${t.id}`}>
                  <div>
                    <p className="text-white font-semibold flex items-center gap-2">
                      <Hash className="w-3.5 h-3.5 text-slate-400" />{t.room_name}
                      <span className="chip text-xs" style={{ background: `${st.color}22`, border: `1px solid ${st.color}66`, color: st.color }}>{st.label}</span>
                    </p>
                    <p className="text-slate-500 text-xs">
                      by {t.organizer_name} · {t.proposed_date}{t.proposed_time ? ` @ ${t.proposed_time}` : ""} · {t.joiner_count} joined{t.note ? ` — "${t.note}"` : ""}
                    </p>
                  </div>
                  <button onClick={() => deleteTentative(t)} disabled={busy === t.id} className="neon-outline-btn px-4 py-2 text-sm self-start sm:self-auto" data-testid={`tentative-delete-${t.id}`}>
                    {busy === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4" /> Delete</>}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <TableWrap>
        <thead><Tr><Th>Channel</Th><Th>Visibility</Th><Th>Description</Th><Th></Th></Tr></thead>
        <tbody>
          {rooms.length === 0 ? <EmptyRow colSpan={4} text="No channels yet" /> : rooms.map((r) => (
            <Tr key={r.id}>
              <Td className="text-white font-semibold" data-testid={`channel-name-${r.id}`}><span className="inline-flex items-center gap-2"><Hash className="w-4 h-4 text-slate-500" />{r.name}</span></Td>
              <Td>
                {r.is_public
                  ? <span className="chip" style={{ background: "rgba(0,229,255,0.14)", border: "1px solid rgba(0,229,255,0.4)", color: "#00e5ff" }}><Globe className="w-3.5 h-3.5" /> Public</span>
                  : <span className="chip" style={{ background: "rgba(255,46,151,0.14)", border: "1px solid rgba(255,46,151,0.4)", color: "#ff2e97" }}><Lock className="w-3.5 h-3.5" /> Private</span>}
              </Td>
              <Td className="text-slate-400 max-w-xs truncate">{r.description || "—"}</Td>
              <Td>
                <button onClick={() => { setForm({ id: r.id, name: r.name, description: r.description || "", is_public: !!r.is_public }); setModalOpen(true); }} className="text-slate-400 hover:text-cyan-400 mr-3" title="Edit" data-testid={`channel-edit-${r.id}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => deactivate(r.id, r.name)} disabled={busy === r.id} className="text-slate-400 hover:text-pink-400" title="Deactivate" data-testid={`channel-delete-${r.id}`}>
                  {busy === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              </Td>
            </Tr>
          ))}
        </tbody>
      </TableWrap>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={form.id ? "Edit Channel" : "Create Channel"} testId="channel-modal">
        <Field label="Name"><input className="input-cyber" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="channel-form-name" /></Field>
        <Field label="Description"><textarea className="input-cyber min-h-[60px] resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="channel-form-desc" /></Field>
        <Field label="Visibility">
          <div className="flex gap-2">
            <button type="button" onClick={() => setForm({ ...form, is_public: true })} className="chip flex-1 justify-center py-2.5" data-testid="channel-form-public"
              style={{ background: form.is_public ? "rgba(0,229,255,0.15)" : "rgba(8,11,22,0.6)", border: `1px solid ${form.is_public ? "rgba(0,229,255,0.6)" : "rgba(120,150,220,0.2)"}`, color: form.is_public ? "#00e5ff" : "#94a3b8" }}>
              <Globe className="w-4 h-4" /> Public
            </button>
            <button type="button" onClick={() => setForm({ ...form, is_public: false })} className="chip flex-1 justify-center py-2.5" data-testid="channel-form-private"
              style={{ background: !form.is_public ? "rgba(255,46,151,0.15)" : "rgba(8,11,22,0.6)", border: `1px solid ${!form.is_public ? "rgba(255,46,151,0.6)" : "rgba(120,150,220,0.2)"}`, color: !form.is_public ? "#ff2e97" : "#94a3b8" }}>
              <Lock className="w-4 h-4" /> Private
            </button>
          </div>
        </Field>
        <button onClick={create} disabled={saving} className="neon-btn w-full py-3 mt-2" data-testid="channel-form-save">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : (form.id ? "Save Changes" : "Create Channel")}
        </button>
      </Modal>
    </div>
  );
}
